import { createAsyncThunk } from '@reduxjs/toolkit';
import { getAPI } from 'services/api/apiAsync';
import { API_ENDPOINTS } from 'utils/constants';
import type { AppDispatch } from 'app/store';
import type {
  FetchTransactionsParams,
  LinkedItem,
  SpendingTransaction,
  TransactionPageResponse,
} from './types';

const PAGE_SIZE = 100;

/**
 * Fetch the list of linked bank items. One-shot on page entry; no cancellation
 * needed (the response has no dependency on the currently selected time-frame).
 */
export const fetchLinkedItems = createAsyncThunk<
  LinkedItem[],
  void,
  { dispatch: AppDispatch; rejectValue: unknown }
>('spending/fetchLinkedItems', async (_arg, thunkApi) => {
  try {
    const response = await thunkApi
      .dispatch(
        getAPI({
          url: API_ENDPOINTS.ftmItems,
          signal: thunkApi.signal,
        }),
      )
      .unwrap();
    return Array.isArray(response) ? (response as LinkedItem[]) : [];
  } catch (err) {
    return thunkApi.rejectWithValue(err);
  }
});

/**
 * Fetch every page of transactions for the selected range concurrently, filter
 * to positive-amount rows, and merge into a single list.
 *
 * Cancellation contract: `thunkApi.signal` is forwarded into every getAPI call.
 * When SpendingTrackerPage's useEffect cleanup calls `promise.abort()`, all
 * in-flight axios requests reject with AbortError, `Promise.all` rejects, and
 * createAsyncThunk dispatches `rejected` with `meta.aborted === true`.
 * The reducer detects that flag and does NOT clear transactions to prevent
 * flash-of-empty on rapid timeframe changes.
 */
export const fetchSpendingTransactions = createAsyncThunk<
  SpendingTransaction[],
  FetchTransactionsParams,
  { dispatch: AppDispatch; rejectValue: unknown }
>('spending/fetchTransactions', async (params, thunkApi) => {
  try {
    // API expects `dateStart` / `dateEnd` — matches
    // identityWeb/.../transaction-monitoring.service.ts getSpendingData
    // (line 183-184). Sending `startDate` / `endDate` here is silently
    // ignored by the backend, which then returns the default (unfiltered)
    // transaction set — every dropdown selection would show the same data.
    const baseParams = {
      dateStart: params.startDate,
      dateEnd: params.endDate,
      pageSize: PAGE_SIZE,
    };

    const firstPage = (await thunkApi
      .dispatch(
        getAPI({
          url: API_ENDPOINTS.ftmTransactions,
          params: { ...baseParams, page: 0 },
          signal: thunkApi.signal,
        }),
      )
      .unwrap()) as TransactionPageResponse;

    const totalPages = firstPage?.totalPages ?? 0;
    if (totalPages <= 1) {
      const firstPageTxns = firstPage?.transactions ?? [];
      return firstPageTxns.filter((t) => t.amount > 0);
    }

    const pageIndices = Array.from(
      { length: totalPages - 1 },
      (_unused, i) => i + 1,
    );
    const remainingPages = (await Promise.all(
      pageIndices.map((page) =>
        thunkApi
          .dispatch(
            getAPI({
              url: API_ENDPOINTS.ftmTransactions,
              params: { ...baseParams, page },
              signal: thunkApi.signal,
            }),
          )
          .unwrap(),
      ),
    )) as TransactionPageResponse[];

    const merged = [firstPage, ...remainingPages].flatMap(
      (p) => p?.transactions ?? [],
    );
    return merged.filter((t) => t.amount > 0);
  } catch (err) {
    return thunkApi.rejectWithValue(err);
  }
});
